import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { SessionSttConfig, SttProviderKeyState } from "../../shared/sttConfig.ts";
import type { SessionLifecycleStatus } from "../../shared/types.ts";
import { getAudioIngressUrl } from "../lib/api.ts";
import type { RoleAuth } from "../lib/auth.ts";
import {
  createAudioCaptureController,
  getMicrophonePermissionState,
  listAudioInputDevices,
  requestAudioInputPermission,
  type AudioCaptureSnapshot,
  type AudioInputDevice,
  type MicrophonePermissionState,
} from "../lib/audioStream.ts";
import {
  prepareLiveCaptureSession,
  shouldStopCaptureForSessionChange,
  shouldStopCaptureForSessionStatus,
} from "./captureLifecycle.ts";
import { liveSttConfigForCapture } from "./sttCaptureConfig.ts";

export interface UseCaptureControllerArgs {
  sessionId: string;
  sessionStatus: SessionLifecycleStatus;
  onInterim: (t: string) => void;
  onLevel?: (level: number) => void;
  auth: RoleAuth;
  stt: SessionSttConfig;
  providerKeys: SttProviderKeyState;
  ingressSharedSecretConfigured: boolean;
  onEnsureSessionActive: (sessionId: string) => Promise<void> | void;
}

export function useCaptureController({
  sessionId,
  sessionStatus,
  onInterim,
  onLevel,
  auth,
  stt,
  providerKeys,
  ingressSharedSecretConfigured,
  onEnsureSessionActive,
}: UseCaptureControllerArgs) {
  const saveSttConfig = useMutation(convexApi.settings.setSttConfig);
  const updateRuntimeStatus = useMutation(convexApi.operatorRuntimeStatus.updateFromOperator);
  const controllerRef = useRef<ReturnType<typeof createAudioCaptureController> | null>(null);
  if (!controllerRef.current) controllerRef.current = createAudioCaptureController();

  const [devices, setDevices] = useState<AudioInputDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<AudioCaptureSnapshot>(() => controllerRef.current!.getSnapshot());
  const [error, setError] = useState<string | null>(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [permissionState, setPermissionState] = useState<MicrophonePermissionState>("unknown");
  const [starting, setStarting] = useState(false);
  const lastRuntimePost = useRef(0);
  const lastRuntimeSignature = useRef("");
  const runtimeMutationRef = useRef(updateRuntimeStatus);
  const runtimeAuthRef = useRef(auth);
  const runtimeSessionIdRef = useRef(sessionId);
  const captureSessionRef = useRef(sessionId);
  const onLevelRef = useRef(onLevel);
  const lastLevelPost = useRef(0);

  useEffect(() => {
    runtimeMutationRef.current = updateRuntimeStatus;
    runtimeAuthRef.current = auth;
    runtimeSessionIdRef.current = sessionId;
    onLevelRef.current = onLevel;
  }, [auth, sessionId, updateRuntimeStatus, onLevel]);

  useEffect(() => {
    const controller = controllerRef.current!;
    return controller.subscribe((next) => {
      setSnapshot(next);
      postRuntimeStatus(next);
      reportLevel(next);
    });
  }, []);

  useEffect(() => {
    void refreshDevices(false);
    void getMicrophonePermissionState().then(setPermissionState).catch(() => setPermissionState("unknown"));
    return () => {
      void controllerRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    const previousSessionId = captureSessionRef.current;
    captureSessionRef.current = sessionId;
    if (!shouldStopCaptureForSessionChange({
      previousSessionId,
      nextSessionId: sessionId,
      captureActive: controllerRef.current?.getSnapshot().micActive ?? false,
    })) return;

    onInterim("");
    void controllerRef.current?.stop();
  }, [sessionId, onInterim]);

  useEffect(() => {
    if (!shouldStopCaptureForSessionStatus({
      sessionStatus,
      captureActive: controllerRef.current?.getSnapshot().micActive ?? false,
    })) return;

    onInterim("");
    void controllerRef.current?.stop();
  }, [sessionStatus, onInterim]);

  async function refreshDevices(requestPermission: boolean): Promise<void> {
    setLoadingDevices(true);
    setError(null);
    try {
      if (requestPermission) await requestAudioInputPermission();
      const nextPermission = await getMicrophonePermissionState();
      setPermissionState(nextPermission === "unknown" && requestPermission ? "granted" : nextPermission);
      const nextDevices = await listAudioInputDevices();
      setDevices(nextDevices);
      setSelectedDeviceId((current) => {
        if (current && nextDevices.some((device) => device.deviceId === current)) return current;
        return nextDevices[0]?.deviceId ?? null;
      });
      if (nextDevices.length === 0) setError("No browser audio input devices were found.");
    } catch (err) {
      const nextPermission = await getMicrophonePermissionState();
      setPermissionState(nextPermission);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingDevices(false);
    }
  }

  async function start(): Promise<void> {
    setStarting(true);
    setError(null);
    onInterim("");
    try {
      if (devices.length === 0) await refreshDevices(true);
      const liveConfig = liveSttConfigForCapture(stt, providerKeys, ingressSharedSecretConfigured);
      await prepareLiveCaptureSession({
        sessionId,
        liveConfig,
        ensureSessionActive: onEnsureSessionActive,
        saveSttConfig: async (config) => {
          await saveSttConfig({
            auth,
            sessionId: sessionId as Id<"sessions">,
            config,
          });
        },
      });
      await controllerRef.current!.start({
        sessionId,
        deviceId: selectedDeviceId,
        ingressUrl: getAudioIngressUrl(),
        auth,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setStarting(false);
    }
  }

  async function stop(): Promise<void> {
    onInterim("");
    await controllerRef.current?.stop();
  }

  function pause(): void {
    onInterim("");
    controllerRef.current?.pause();
  }

  function resume(): void {
    controllerRef.current?.resume();
  }

  function reportLevel(next: AudioCaptureSnapshot): void {
    const report = onLevelRef.current;
    if (!report) return;
    const now = Date.now();
    if (now - lastLevelPost.current < 100) return;
    lastLevelPost.current = now;
    report(next.micActive && !next.micPaused ? next.micLevel : 0);
  }

  function postRuntimeStatus(next: AudioCaptureSnapshot): void {
    const targetSessionId = runtimeSessionIdRef.current;
    const signature = [
      targetSessionId,
      next.micActive,
      next.micPaused,
      next.selectedInputDeviceId,
      next.selectedInputDeviceLabel,
      next.audioTransport,
      next.audioTransportError,
      next.sttTransport,
      next.sttError,
      next.lastSttActivityAt,
      next.lastTranscriptAt,
    ].join("|");
    const now = Date.now();
    if (signature === lastRuntimeSignature.current && now - lastRuntimePost.current < 400) return;
    lastRuntimeSignature.current = signature;
    lastRuntimePost.current = now;
    void runtimeMutationRef.current({
      auth: runtimeAuthRef.current,
      sessionId: targetSessionId as Id<"sessions">,
      patch: {
        micActive: next.micActive,
        micPaused: next.micPaused,
        selectedInputDeviceId: next.selectedInputDeviceId,
        selectedInputDeviceLabel: next.selectedInputDeviceLabel,
        audioTransport: next.audioTransport,
        audioTransportError: next.audioTransportError,
        sttTransport: next.sttTransport,
        sttError: next.sttError,
        lastSttActivityAt: next.lastSttActivityAt,
        lastTranscriptAt: next.lastTranscriptAt,
      },
    }).catch(() => {
      /* runtime status is best-effort; capture state remains local */
    });
  }

  const active = snapshot.micActive;
  const canRefreshDevices = permissionState === "granted" || hasUsableDeviceLabels(devices);
  const deviceButtonLabel = loadingDevices
    ? "Checking..."
    : canRefreshDevices
      ? "Rescan audio inputs"
      : "Allow mic access";

  return {
    active,
    paused: snapshot.micPaused,
    snapshot,
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    error,
    loadingDevices,
    permissionState,
    starting,
    canRefreshDevices,
    deviceButtonLabel,
    refreshDevices,
    start,
    stop,
    pause,
    resume,
  };
}

export type CaptureControllerState = ReturnType<typeof useCaptureController>;

export function hasUsableDeviceLabels(devices: AudioInputDevice[]): boolean {
  return devices.some((device) => device.label.trim() && !/^Input \d+$/.test(device.label));
}
